/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'December 17, 2025';

export default function Privacy() {
  return (
    <div className="hp-legal">
      <header className="hp-legal-header">
        <Link to="/" className="hp-legal-back" data-testid="legal-back">
          ← Back
        </Link>
        <p className="hp-eyebrow">
          <span className="hp-dot" />
          The Hall Pass · Hayward, CA
        </p>
        <h1 className="hp-legal-title">Privacy Policy</h1>
        <p className="hp-legal-meta">Effective: {EFFECTIVE_DATE}</p>
      </header>

      <main className="hp-legal-body" data-testid="privacy-content">
        <section>
          <h2>1. Introduction</h2>
          <p>
            This Privacy Policy describes how The Hall Pass, including its
            owners, operators, affiliates, parents, subsidiaries, members,
            managers, officers, employees, agents, contractors, and
            representatives (collectively, "The Hall Pass," "we," "us," or
            "our") collects, uses, discloses, and protects personal information
            of individuals ("you" or "your") who visit our pre-launch landing
            page or otherwise interact with us in connection with the proposed
            Hall Pass venue and brand to be located in or around Hayward,
            California (the "Services"). This policy applies to information
            collected through this site, by phone, by email, by SMS/MMS, and
            through any related forms or events.
          </p>
          <p>
            By submitting information to us or using the Services, you
            acknowledge that you have read and understood this Privacy Policy.
            If you do not agree, please do not submit information or use the
            Services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>
            <strong>Information you provide directly</strong>: first name, last
            name, email address, telephone number, referral source (how you
            heard about us), marketing consent status, and any other
            information you voluntarily submit through our reservation form,
            messages, replies to our SMS, or correspondence with us.
          </p>
          <p>
            <strong>Information collected automatically</strong>: IP address,
            general geographic location derived from IP, browser type and
            version, device identifiers, operating system, referring URL, pages
            viewed, timestamps, and similar technical and analytics data
            necessary for security, fraud prevention, abuse prevention, and
            site operation. We may use cookies, pixels, and similar
            technologies for these purposes.
          </p>
          <p>
            <strong>Information from third parties</strong>: where applicable,
            information from email delivery, SMS gateway, hosting, analytics,
            or fraud-prevention service providers acting on our behalf.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <ul>
            <li>To add you to our pre-launch interest list and reservations.</li>
            <li>
              To send you launch announcements, opening details, reservation
              information, event invitations, promotions, and related updates
              by email and/or SMS/MMS, in each case where you have provided the
              applicable consent.
            </li>
            <li>To respond to inquiries and provide customer support.</li>
            <li>
              To operate, secure, and improve the Services, prevent fraud and
              abuse, and ensure technical functionality.
            </li>
            <li>
              To comply with legal obligations, court orders, subpoenas, and
              regulatory requests, and to exercise or defend legal claims.
            </li>
            <li>
              For analytics and aggregated reporting, including measuring
              campaign performance and visitor engagement.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. SMS / Text Messaging Program</h2>
          <p>
            By checking the consent box on our reservation form and submitting
            your phone number, you expressly consent to receive recurring
            automated marketing and transactional text messages (including
            SMS/MMS) from The Hall Pass at the mobile number you provided.
            Consent is not a condition of any purchase. Message and data rates
            may apply. Message frequency may vary. You may receive a one-time
            confirmation text to complete your opt-in.
          </p>
          <p>
            Reply <strong>STOP</strong> at any time to unsubscribe from our
            text messages. Reply <strong>HELP</strong> for help. For additional
            help, contact us at the email address below. Carriers are not
            liable for delayed or undelivered messages.
          </p>
        </section>

        <section>
          <h2>5. Email Marketing</h2>
          <p>
            We send promotional and transactional emails consistent with the
            U.S. CAN-SPAM Act. Every marketing email includes an unsubscribe
            link. You may opt out at any time, and we will honor the request
            within the timeframes required by law.
          </p>
        </section>

        <section>
          <h2>6. How We Share Information</h2>
          <p>We do not sell your personal information for money. We may share information with:</p>
          <ul>
            <li>
              <strong>Service providers</strong>: email and SMS platforms,
              hosting providers, analytics providers, customer-relationship
              tools, fraud-prevention and security providers, and similar
              vendors that process information on our behalf and are
              contractually limited to that purpose.
            </li>
            <li>
              <strong>Affiliates and subsidiaries</strong>: our parents,
              affiliates, subsidiaries, and related entities for the purposes
              described in this Policy.
            </li>
            <li>
              <strong>Legal and safety</strong>: government authorities, law
              enforcement, courts, regulators, and other parties when required
              by law, subpoena, or court order, or where we believe in good
              faith that disclosure is necessary to investigate, prevent, or
              respond to illegal activity, fraud, threats to safety, or to
              protect our legal rights.
            </li>
            <li>
              <strong>Business transfers</strong>: in connection with a merger,
              acquisition, financing, restructuring, sale of assets, joint
              venture, or similar transaction, your information may be
              transferred as a business asset.
            </li>
            <li>
              <strong>With your consent</strong>: any other party with your
              direct consent.
            </li>
          </ul>
          <p>
            We do not share telephone numbers collected for SMS marketing with
            third parties or affiliates for their own marketing purposes.
          </p>
        </section>

        <section>
          <h2>7. Cookies & Tracking</h2>
          <p>
            We may use cookies, pixels, web beacons, local storage, and similar
            technologies to operate the site, remember preferences, evaluate
            site performance, and support marketing. Most browsers allow you to
            block or delete cookies through browser settings; blocking cookies
            may affect site functionality. We currently do not change behavior
            in response to Do-Not-Track browser signals; we honor recognized
            opt-out preference signals (such as Global Privacy Control) where
            required by applicable law.
          </p>
        </section>

        <section>
          <h2>8. Data Retention</h2>
          <p>
            We retain personal information for as long as reasonably necessary
            to fulfill the purposes described in this Policy, to comply with
            legal obligations, to resolve disputes, and to enforce agreements.
            We may retain de-identified or aggregated information indefinitely.
          </p>
        </section>

        <section>
          <h2>9. Security</h2>
          <p>
            We use reasonable administrative, technical, and physical
            safeguards designed to protect personal information. No system is
            perfectly secure, however, and we cannot guarantee absolute
            security. You provide information at your own risk.
          </p>
        </section>

        <section>
          <h2>10. Your Privacy Rights (All 50 U.S. States)</h2>
          <p>
            Depending on your state of residence and applicable law, including
            without limitation the California Consumer Privacy Act and
            California Privacy Rights Act ("CCPA/CPRA"), the Virginia Consumer
            Data Protection Act ("VCDPA"), the Colorado Privacy Act ("CPA"),
            the Connecticut Data Privacy Act ("CTDPA"), the Utah Consumer
            Privacy Act ("UCPA"), and similar laws in Texas, Oregon, Montana,
            Iowa, Indiana, Tennessee, Delaware, New Jersey, New Hampshire,
            Kentucky, Maryland, Minnesota, Nebraska, Rhode Island, and other
            states, you may have the right to:
          </p>
          <ul>
            <li>Know or access the personal information we hold about you.</li>
            <li>Request correction of inaccurate personal information.</li>
            <li>
              Request deletion of your personal information, subject to legal
              exceptions.
            </li>
            <li>
              Opt out of "sale" or "sharing" of personal information (we do not
              sell personal information for money; if a state law treats any
              targeted-advertising or cross-context behavioral activity as a
              "sale" or "sharing," you may opt out as described below).
            </li>
            <li>
              Opt out of profiling that produces legal or similarly significant
              effects.
            </li>
            <li>Limit use of any "sensitive" personal information.</li>
            <li>
              Receive a portable copy of personal information you provided to
              us.
            </li>
            <li>
              Be free from unlawful discrimination for exercising your privacy
              rights.
            </li>
            <li>
              Appeal a decision we make about your privacy request (where
              applicable).
            </li>
          </ul>
          <p>
            <strong>How to exercise your rights</strong>: send a request to{' '}
            <a href="mailto:privacy@thehallpass.com">privacy@thehallpass.com</a>{' '}
            with the subject line "Privacy Request" and identify your state of
            residence and the right you wish to exercise. We will verify your
            identity using reasonable methods before responding. You may
            authorize an agent to act on your behalf; we may require proof of
            authorization and verification of your identity.
          </p>
          <p>
            <strong>"Do Not Sell or Share / Limit Use" (California and
            similar)</strong>: to opt out of any sharing for cross-context
            behavioral advertising or any "sale" as defined by your state law,
            and to limit use of sensitive personal information, email{' '}
            <a href="mailto:privacy@thehallpass.com">privacy@thehallpass.com</a>{' '}
            with the subject line "Do Not Sell or Share."
          </p>
          <p>
            <strong>California "Shine the Light"</strong>: California
            residents may request information about disclosures, if any, of
            personal information to third parties for their direct marketing
            purposes. We do not currently make such disclosures.
          </p>
          <p>
            <strong>Nevada residents</strong>: Nevada residents may opt out of
            the sale of certain personal information by emailing the address
            above. We do not currently sell personal information.
          </p>
        </section>

        <section>
          <h2>11. Children</h2>
          <p>
            The Services are intended for adults of legal drinking age in the
            United States (21 years or older). We do not knowingly collect
            personal information from anyone under 13 (or under 16 where
            applicable). If we learn we have collected such information, we
            will delete it.
          </p>
        </section>

        <section>
          <h2>12. International Visitors</h2>
          <p>
            Our Services are intended for users in the United States.
            Information is processed in the United States. If you access the
            Services from outside the United States, you understand and
            consent to the transfer of your information to the United States,
            which may have different data-protection laws than your country.
          </p>
        </section>

        <section>
          <h2>13. Third-Party Links</h2>
          <p>
            The Services may link to third-party websites or services. We are
            not responsible for the privacy practices or content of those
            third parties. Please review their privacy policies separately.
          </p>
        </section>

        <section>
          <h2>14. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The "Effective"
            date above indicates when it was last revised. Material changes
            will be communicated as required by law.
          </p>
        </section>

        <section>
          <h2>15. Contact Us</h2>
          <p>
            The Hall Pass<br />
            Hayward, California, United States<br />
            Email:{' '}
            <a href="mailto:privacy@thehallpass.com">privacy@thehallpass.com</a>
          </p>
        </section>

        <section className="hp-legal-disclaimer">
          <p>
            <strong>Legal Notice</strong>: This Privacy Policy is provided as a
            starting template tailored to a U.S. pre-launch landing page
            collecting marketing-list information. It is not legal advice. You
            should have qualified counsel review and customize this document
            for your specific business operations, jurisdictions, and data
            practices before launch.
          </p>
        </section>
      </main>

      <footer className="hp-legal-footer">
        <Link to="/" className="hp-legal-link">Home</Link>
        <Link to="/terms" className="hp-legal-link">Terms of Service</Link>
      </footer>
    </div>
  );
}
